import { IParamsCreateFileObject } from '@/courses/interfaces/params-create-file-object.interface';
import Vue from 'vue';
import Component from 'vue-class-component';
import { Mixins, Prop, Emit } from 'vue-property-decorator';
import KEYS from '@/values/keys';
import EdFormModel from '@/models/ed-form.model';
import EdInputModel from '@/models/ed-input.model';
import { required, maxLength, decimal, numeric, between  } from 'vuelidate/lib/validators'
import { SECTION_TYPES } from '@/content/values/sections';
import { IResultCreateFileObject } from '@/courses/interfaces/result-create-file-object.interface';
import ToolService from '@/providers/tools';
import { SectionProvider } from '@/sections/providers/section.provider';
import SectionSchoolCalendarMixin from '@/sections/mixins/section-school-calendar.mixin';
import { Section } from '@/courses/models/section.model';
import EdScanDocuments from '@/courses/components/file-object/ed-scan-documents/ed-scan-documents.component.vue';
import EdSelectComponentCourse from '@/course-templates/components/ed-select-component-course/ed-select-component-course.component.vue';
import {IParamsCreateComponent} from '@/courses/interfaces/params-create-component.interface';
import Course from '@/content/models/course.model';
import { CourseToolsMixin } from '@/courses/mixins/course-tools.mixin';
import EdTreeview from '@/components/common/ed-treeview/ed-treeview.component.vue';
import JSZip from 'jszip';

@Component({
  components: {
    EdSelectComponentCourse,
    EdScanDocuments,
    EdTreeview,
    EdSelectTag: () => import(/* EdSelectTag */'@/courses/components/ed-select-tag/ed-select-tag.component.vue')
  }
})
export default class EdCreateFileObject extends Mixins(SectionSchoolCalendarMixin, CourseToolsMixin) {

  //@Prop() readonly params: IParamsCreateFileObject;   //Typescript upgrade
  declare readonly params: IParamsCreateFileObject;
  @Prop() readonly close: Function;
  @Prop() readonly save: Function;
  @Prop() readonly hasGrading: boolean;
  @Prop() readonly section: Section;
  @Prop() readonly rotateDegreesInfo: any[];
  @Prop() readonly course: Course;
  @Prop({ default: false }) hiddenTaskFields: boolean;
  @Prop() readonly acceptedFiles: any;


  public dropzoneMode = KEYS.DROPZONE_CONFIG.SINGLE;
  public dropzoneOptions: any = {
    url: 'http://eduac.com',
    acceptedFiles: 'application/pdf,.docx,.doc,.xlsx,.xls,.pptx,.ppt,.epub,.zip',
    autoProcessQueue: false,
    addRemoveLinks: true,
    uploadMultiple: false,
    maxFiles: 1,
    parallelUploads: 1,
    createImageThumbnails: false,
    maxFilesize: 200,
    timeout: 600000
  };
  public form: any = EdFormModel([
    EdInputModel('name', '', { required }),
    EdInputModel('description', ''),
    EdInputModel('homework', false, {}, true),
    EdInputModel('homeworkPoints', 100, {}, true),
    EdInputModel('homeworkWeight', 1, {}, true)
  ]);
  public type: string = SECTION_TYPES.LESSON_TEMP_FILE.code;
  public file: any = null;
  public saving: boolean = false;
  public ext: string = '';
  public hasErrorFile: boolean = false;
  public removingFile: boolean = false;
  public fileAdded: boolean = false;
  public fileChanged: boolean = false;
  public tags: Array<string> = ['TEST', 'CLASSWORK', 'HOMEWORK'];
  public tagSelected: string = '';
  public htmlFile: boolean = false;
  public canScanDocument: boolean = false;
  public saveAsPdf: boolean = false;
  public showFileIcons: boolean = true;
  public showHomeworkByScan: boolean = true;
  public components: IParamsCreateComponent[];
  public showEduacComponentModal: boolean = false;
  public paramsComponent: any = {};
  public canShowRotation: boolean = false;
  public showTreeList: boolean = false;
  public itemsTree : any = [];
  public fileAllowed : boolean = true;

  @Emit('onRemoveRotationData')
  emitOnRemoveRotationData() {
    return true;
  }

  @Emit('onChangePdfUrl')
  onChangePdfUrl() {
    this.canShowRotation = true;
    return true;
  }

  get title() {
    return this.params.title;
  }

  get canSetHomework() {
    return this.params.homework.show;
  }

  get canSetGrade() {
    return this.params.grade.show;
  }

  get preload() {
    return this.params.preload;
  }

  get hasDropzone() {
    return !this.file;
  }

  get escapedName() {
    return ToolService.exitSpecialChars(this.form.name.value);
  }

  get isHomework() {
    if(this.form) {
      return this.form.get('homework') ? this.form.get('homework').value : false;
    }
    return false;
  }

  get showRotation() {
    return this.params.edition && !this.fileChanged && !this.htmlFile && !this.isPdfImage;
  }

  get shouldArchiveCurrentHomeworks(){
    return this.fileChanged;
  }

  get hasBeenResolvedByAnyStudent(){
    return this.params.hasBeenResolvedByAnyStudent ? this.params.hasBeenResolvedByAnyStudent : false;
  }

  get isPdfImage() {
    return SECTION_TYPES.LESSON_PDF_IMAGE.code === this.section?.type;
  }

  get showFileAndScanIcons() {
    return this.showFileIcons;
  }

  get isFormInvalid() {
    return !this.form.valid || !this.file || this.saving || (!this.fileAllowed && this.showTreeList && this.hiddenTaskFields);
  }

  get isUrlLoaded() {
    return (this.params.url || this.canShowRotation) ? true : false;
  }

  get showConfirmMessageByRotation() {
    return (this.rotateDegreesInfo && this.rotateDegreesInfo.length > 0);
  }

  get msgFileAllowedError() {
    let msg = String(this.$i18n.t('courses.lesson.allowedFilesError'));
    return msg.replace('{{otherExtensions}}', String(this.$store.state.AppStore.fileExtensionsNotAllowed.join(', ')));
  }

  get courseType(): string {
    return this.$store.state.CourseStore.courses.content.find(
      (course: any) => course.id === this.params.courseId
    )?.type;
  }

  get showDisclaimer(): boolean {
    return this.params.edition && this.fileChanged && ![KEYS.CONTENT_TYPE.COURSE_TEMPLATE, KEYS.CONTENT_TYPE.CST].includes(this.courseType);
  }

  get componentSelected() : IParamsCreateComponent {
    return this.$store.state.CourseStore.eduacComponentSelected;
  }

  set componentSelected(newValue : IParamsCreateComponent) {
    this.$Courses.Store.setEduacComponentSelected(newValue);
  }

  initForm(name: string = '', homework: boolean = false, dueDate: string = '', lastDate: string = '', homeworkPoints: number = 100, homeworkWeight: number = 1) {
    this.form = EdFormModel([
      EdInputModel('name', name, { required, maxLength: maxLength(60) }),
      EdInputModel('homework', homework, {}, true),
      EdInputModel('dueDate', dueDate, {}),
      EdInputModel('lastDate', lastDate, {}),
      EdInputModel('homeworkPoints', homeworkPoints, { numeric, between: between(1, 1000)}, true),
      EdInputModel('homeworkWeight', homeworkWeight, { numeric, between: between(1, 1000)}, true)
    ]);
    if(homework && this.hasGrading && !this.hiddenTaskFields){
      this.form.setValidator('homeworkPoints', 'required', required);
      this.form.setValidator('homeworkWeight', 'required', required);
    }
  }

  async showUnzipElements(file) {
    let zip = new JSZip();
    const content = await zip.loadAsync(file);
    let listToConvert: string[] = [];
  
    zip.forEach((relativePath, file) => {
      if (!relativePath.includes('__MACOSX') && !relativePath.includes('.DS_Store')) {
        listToConvert.push(relativePath);
      }
    });
  
    listToConvert.sort((a, b) => a.localeCompare(b)); // Sort paths
  
    let objectTree = this.listToTree(listToConvert, this.form.name.value);
  
    this.sortTree(objectTree); // Sort tree nodes
  
    this.itemsTree.push(objectTree);
    this.showTreeList = true;
  }
  
  sortTree(node) {
    if (node.children && node.children.length) {
      node.children.sort((a, b) => {
        if (a.file !== b.file) return a.file ? 1 : -1;
        return a.name.localeCompare(b.name);
      });
      node.children.forEach(child => this.sortTree(child));
    }
  }
  
  listToTree(list: string[], rootName: string) {
    const tree = {
      name: rootName,
      children: [],
      file: false
    };
  
    for (const str of list) {
      let splits = str.split('/').filter(f => f);
      let pathKey = '';
      let parent = tree;
  
      splits.forEach((place, index) => {
        pathKey += '/' + place;
  
        let existing = parent.children.find(c => c.name === place && (c.pathKey === pathKey));
  
        if (!existing) {
          this.validateOtherFileExtension(place)
          let isFile = !!this.getExtension(place);
  
          const node = {
            name: place,
            children: isFile ? undefined : [],
            file: isFile,
            pathKey: pathKey 
          };
  
          parent.children.push(node);
          existing = node;
        }
  
        parent = existing;
      });
    }
  
    return tree;
  }

  validateOtherFileExtension(fileName) {
    let extFile = this.getExtension(fileName);
    if(this.$store.state.AppStore.fileExtensionsNotAllowed.includes('.' + extFile)) {
      this.fileAllowed = false;
      return true;
    }
    return false;
  }

  onFileAdd(file: any) {
    this.hasErrorFile = false;
    setTimeout(() => {
      if(file.accepted) {
        this.ext = this.getExtension(file.name);
        this.file = file;
        if(!this.form.get('name').value || this.hiddenTaskFields) this.setFileName(file.name);
        this.fileAdded = true;
        this.htmlFile = false;
        if(SECTION_TYPES.LESSON_PPT_HTML.extensions.includes(this.ext.toUpperCase())) {
          this.type = SECTION_TYPES.LESSON_PPT_HTML.code;
          this.form.set('homework', false);
          this.htmlFile = true;
        } else if(this.params.edition) {
          this.type = SectionProvider.getTypeFromExtension(this.ext);
        }
        this.showUnzipElements(file);
      }
    }, 0);
  }

  setFileName(filename: string) {
    let nameParts = filename.split('.');
    let name = filename.replace(
      `.${nameParts[nameParts.length - 1]}`,
      ''
    ).slice(0, 60);
    Vue.set(this.form, 'name', {
      valid: true,
      validators: { required, maxLength: maxLength(60) },
      value: name
    });
  }

  getExtension(filename) {
    let fileArr = filename.split('.');
    return fileArr.length > 1 && fileArr[fileArr.length - 1] ? fileArr[fileArr.length - 1] : '';
  }

  onErrorFile() {
    this.hasErrorFile = true;
    this.removeFile();
  }

  removeFile() {
    let refs: any = this.$refs;
    refs.dropzoneRef?.removeAllFiles();
    if(refs.scanDocuments) {
      refs.scanDocuments.removeImg();
      this.form.homework = false;
    }
    this.file = null;
    this.fileChanged = true;
    this.htmlFile = false;
    this.showFileIcons = true;
    this.emitOnRemoveRotationData();
    this.showTreeList = false;
    this.itemsTree = [];
    this.fileAllowed = true;
  }

  showScanDocument() {
    this.file = null;
    this.canScanDocument = true;
    this.showHomeworkByScan = false;
  }

  showFileUploadDocument() {
    this.file = null;
    this.canScanDocument = false;
    this.saveAsPdf = false;
    this.showHomeworkByScan = true;
  }

  changeFileScan(data) {
    this.file = data?.file;
    this.type = SECTION_TYPES.LESSON_PDF_IMAGE.code;
    this.ext = 'png';
    this.saveAsPdf = data?.saveAsPdf;
    this.showHomeworkByScan = data?.saveAsPdf;
    this.fileAdded = true;
  }

  rotateFile(){
    this.$emit('rotatePages');
  }

  getPdfUrl() {
    if(!this.isUrlLoaded) {
      let fileData = { sectionId: this.section.sectionId , materialId: null ,
        courseId: this.section.courseId , classId: this.section.classId};
      this.deleteFromStorage(fileData.sectionId , null);
      let isProcessing = this.checkStorage(fileData.sectionId , null);
      if(isProcessing) {
        let intervalId : any = setInterval((data) => {
          this.waitForReadyFileInfo(intervalId , data).then(
            (status) => {
              if(status != 206) {
                this.onChangePdfUrl();
              }
            }
          );
        }, 15000 , fileData);
      }
    }
  }

  created() {
    if(this.params.edition || this.preload) {
      this.showHomeworkByScan = true;
      this.showFileIcons = false;
      this.file = this.params.file;
      this.ext = this.params.ext;
      this.tagSelected = this.params.labelSections;
      if(SECTION_TYPES.LESSON_PPT_HTML.extensions.includes(this.ext.toUpperCase())) {
        this.type = SECTION_TYPES.LESSON_PPT_HTML.code;
        this.form.set('homework', false);
        this.htmlFile = true;
      } else if(this.isPdfImage) {
        if(this.section?.assetsInfo && !this.section.assetsInfo[0].pdfUrl) {
          this.showHomeworkByScan = false;
        } else {
          this.saveAsPdf = true;
        }
        this.type = SECTION_TYPES.LESSON_PDF_IMAGE.code;
      }
      this.initForm(
        this.params.name,
        this.params.homework.value,
        this.params.dueDate,
        this.params.lastDate,
        this.params.homeworkPoints,
        this.params.homeworkWeight
      );

      this.components = this.course.eduacComponents;
      if(this.section.component) {
        this.componentSelected = this.components.find(c => c.id == this.section.component.id);
      } else if(this.components) {
        this.componentSelected = this.components.find(c => c.mandatory);
      }
      this.getPdfUrl();
    } else {
      this.initForm();
      if(this.course.eduacComponents) {
        this.components = this.course.eduacComponents;
        this.componentSelected = this.components.find(c => c.mandatory);
      }
      
      if(this.acceptedFiles) {
        this.dropzoneOptions.acceptedFiles = this.acceptedFiles;
      }
    }
  }

  closeModal() {
    if(this.canScanDocument) {
      (this.$refs.scanDocuments as any).closeCamera();
    }
    this.$emit('close');
  }

  setDateToSave() {
    if(this.params.edition && this.isPdfImage && !this.saveAsPdf) {
      this.form.homework = false;
    }
    const dueDateInfo = SectionProvider.getDueDateInfo(
      this.form,
      this.defaultDueDate,
      this.defaultLastDate,
      this.params.edition,
      this.params.section,
      this.params.submissionDates
    );
    const payload: any = {
      dueDate: this.form.dueDate.value,
      lastDate: this.form.lastDate.value,
      homework: this.form.homework.value,
      name: this.form.name.value,
      file: this.file,
      fileAdded: this.fileAdded,
      type: this.type,
      ext: this.ext,
      ...dueDateInfo,
      homeworkPoints: this.form.homeworkPoints.value,
      homeworkWeight: this.form.homeworkWeight.value,
      saveAsPdf: this.saveAsPdf,
      labelSections: this.tagSelected,
      component: this.componentSelected
    }
    this.saveFile(payload);
  }

  setSaving(value: boolean) {
    this.saving = value;
  }

  saveFile(fileObject: IResultCreateFileObject) {
    if((this.params.hasBeenResolvedByAnyStudent) && (this.shouldArchiveCurrentHomeworks || this.showConfirmMessageByRotation)) {
      this.$EdModal.createConfirmationModal(
        {
          body: (this.showConfirmMessageByRotation) ? this.$i18n.t('courses.objects.confirmRotateEdit') : this.$i18n.t('courses.objects.confirmEdit'),
          action: async () => {
            this.$emit('save', fileObject);
          }
        }
      );
    } else {
      this.$emit('save', fileObject);
    }
  }

  requireHomeworkPoints(){
    if (this.form.homework.value && this.hasGrading){
      this.form.setValidator('homeworkPoints', 'required', required);
      this.form.setValidator('homeworkWeight', 'required', required);
    } else{
      this.form.removeValidator('homeworkPoints', 'required')
      this.form.removeValidator('homeworkWeight', 'required')
    }
  }

  showPptHtmlDisclamer() {
    this.$EdModal.createConfirmationModal({
      htmlBody: true,
      title: this.$i18n.t('lesson.instructionsTitle'),
      body: this.$i18n.t('lesson.editFilePptHtmlDisclamer'),
      actions: [{label: this.$i18n.t('common.accept')}]
    });
  }

  changeComponent(data) {
    this.componentSelected = data;
  }

}